// The ledger form's state, and turning it into the API's JSON (docs/app.md §8): every section is sent, as an object
// when it applies and has something in it, else null — so moving a ledger to another group clears what no longer fits.
import type { Group, Ledger } from "../lib/api";
import { parseAmount, usualSide, type Side } from "../lib/money";
import { gstinValid, panValid } from "../lib/india";
import { ledgerKind } from "./groups";

export interface BillDraft { ref_name: string; bill_date: string; due_date: string; side: Side; amount: string }
export interface Draft {
  id?: string; name: string; number: string; group_id: string | null; is_party: boolean; party_kind: "customer" | "supplier" | "both" | null; active: boolean;
  cost_centres_on?: boolean;   // 262_a: entries to it are shared over cost centres
  track_people?: "employee" | "student" | "";   // 269_a: entries to it name the employee or student
  currency: string;                                            // kept in a foreign currency ("" = the base currency)
  opening: { side: Side; amount: string };
  billwise: { on: boolean | null; credit_days: string };       // on: null = the group's default
  bills: BillDraft[];
  bank: { bank_name: string; account_no: string; branch_code: string; currency: string };
  gstParty: { reg_type: string; gstin: string; state_code: string; legal_name: string; address: string; place: string; pincode: string; eco?: boolean };
  gstLedger: { taxability: string; hsn_sac: string; rate: string; itc: string };
  gstTax: { on: boolean; registration_id: string; head: string; direction: string; rcm: boolean };
  tdsParty: { pan: string; residence: string; default_provision: string; senior_citizen: boolean; transporter_declaration: boolean;
              country: string; trc_valid_to: string; form_10f: boolean; treaty_rate: string; foreign_tax_id: string };
  tdsLedger: { provision_code: string };
}

const s = (v: unknown) => (v == null ? "" : String(v));

export function emptyDraft(group?: Group): Draft {
  const k = ledgerKind(group);
  return {
    name: "", number: "", group_id: group?.id ?? null, is_party: k.party, party_kind: k.party ? (k.partyKind as Draft["party_kind"]) : null, active: true, currency: "",
    opening: { side: usualSide(group?.nature), amount: "" },
    billwise: { on: null, credit_days: "" }, bills: [],
    bank: { bank_name: "", account_no: "", branch_code: "", currency: "INR" },
    gstParty: { reg_type: "regular", gstin: "", state_code: "", legal_name: "", address: "", place: "", pincode: "" },
    gstLedger: { taxability: "taxable", hsn_sac: "", rate: "", itc: "eligible" },
    gstTax: { on: false, registration_id: "", head: "igst", direction: "output", rcm: false },
    tdsParty: { pan: "", residence: "resident", default_provision: "", senior_citizen: false, transporter_declaration: false,
                country: "", trc_valid_to: "", form_10f: false, treaty_rate: "", foreign_tax_id: "" },
    tdsLedger: { provision_code: "" },
  };
}

export function fromLedger(l: Ledger, group?: Group): Draft {
  const d = emptyDraft(group);
  const tp = (l.tds_party ?? {}) as Record<string, unknown>;
  return {
    ...d, id: l.id, name: l.name, number: l.number ?? "", group_id: l.group_id, is_party: l.is_party, party_kind: l.party_kind, active: l.active, currency: l.currency ?? "", cost_centres_on: l.cost_centres_on, track_people: l.track_people ?? "",
    opening: l.opening ? { side: l.opening.side, amount: String(l.opening.amount) } : d.opening,
    billwise: l.billwise ? { on: l.billwise.enabled, credit_days: l.billwise.credit_days ? String(l.billwise.credit_days) : "" } : d.billwise,
    bills: l.opening_bills.map((b) => ({ ref_name: b.ref_name, bill_date: b.bill_date, due_date: s(b.due_date), side: b.side, amount: String(b.amount) })),
    bank: l.bank ? { bank_name: l.bank.bank_name, account_no: s(l.bank.account_no), branch_code: s(l.bank.branch_code), currency: l.bank.currency } : d.bank,
    gstParty: l.gst_party ? { reg_type: l.gst_party.reg_type, gstin: s(l.gst_party.gstin), state_code: l.gst_party.state_code,
      legal_name: s(l.gst_party.legal_name), address: s(l.gst_party.address), place: s(l.gst_party.place), pincode: s(l.gst_party.pincode),
      eco: !!l.gst_party.ecommerce_operator } : d.gstParty,
    gstLedger: l.gst_ledger ? { taxability: l.gst_ledger.taxability, hsn_sac: s(l.gst_ledger.hsn_sac), rate: s(l.gst_ledger.rate), itc: l.gst_ledger.itc } : d.gstLedger,
    gstTax: l.gst_tax ? { on: true, registration_id: l.gst_tax.registration_id, head: l.gst_tax.head, direction: l.gst_tax.direction, rcm: l.gst_tax.rcm } : d.gstTax,
    tdsParty: l.tds_party ? { pan: s(tp.pan), residence: s(tp.residence) || "resident", default_provision: s(tp.default_provision),
      senior_citizen: !!tp.senior_citizen, transporter_declaration: !!tp.transporter_declaration, country: s(tp.country),
      trc_valid_to: s(tp.trc_valid_to), form_10f: !!tp.form_10f, treaty_rate: s(tp.treaty_rate), foreign_tax_id: s(tp.foreign_tax_id) } : d.tdsParty,
    tdsLedger: l.tds_ledger ? { provision_code: l.tds_ledger.provision_code } : d.tdsLedger,
  };
}

export const GSTIN_TYPES = ["regular", "composition", "sez", "deemed_export"];

// bill-wise as it will be: the ledger's own setting, or the group's default (parties are bill-wise)
export const billwiseOn = (d: Draft, group?: Group) => d.billwise.on ?? ledgerKind(group).party;

export interface Built { payload: Record<string, unknown>; errors: string[] }

export function toPayload(d: Draft, group: Group | undefined, opts: { india: boolean; openingsOpen: boolean }): Built {
  const errors: string[] = [];
  const k = ledgerKind(group);
  const party = d.is_party;
  const num = (v: string, what: string) => {
    const n = parseAmount(v);
    if (Number.isNaN(n)) errors.push(`${what}: enter an amount in rupees and paise`);
    return n == null || Number.isNaN(n) ? null : n;
  };
  if (!d.name.trim()) errors.push("Name is needed");
  if (!d.group_id) errors.push("Choose the group it goes under");
  if (party && !d.party_kind) errors.push("Choose whether the party is a customer, a supplier or both");

  const p: Record<string, unknown> = {
    name: d.name.trim(), group_id: d.group_id, is_party: party, party_kind: party ? d.party_kind : null, active: d.active, cost_centres_on: !!d.cost_centres_on,
  };
  if (d.id) p.id = d.id;
  if (d.track_people !== undefined && !party) p.track_people = d.track_people || null;
  if (d.track_people && party) errors.push("A customer or vendor account can't also be kept by employee or student");
  if (d.number.trim() || d.id) p.number = d.number.trim() || null;
  if (d.number.trim() && !/^[0-9]{3,8}(-[0-9]{1,4})?$/.test(d.number.trim())) errors.push("Number: the group's number, a dash and up to four digits (1310-001)");

  const bw = billwiseOn(d, group);
  const days = d.billwise.credit_days.trim() === "" ? 0 : Number(d.billwise.credit_days);
  if (!Number.isInteger(days) || days < 0 || days > 3650) errors.push("Credit days: a whole number of days");
  p.billwise = d.billwise.on === null && !days ? null : { enabled: bw, credit_days: Number.isInteger(days) ? days : 0 };

  if (opts.openingsOpen) {
    const amt = num(d.opening.amount, "Opening balance");
    p.opening = amt ? { side: d.opening.side, amount: amt } : null;
    const bills = bw ? d.bills.filter((b) => b.ref_name.trim() || b.amount.trim()) : [];
    for (const b of bills) {
      if (!b.ref_name.trim()) errors.push("Every opening bill needs a reference");
      if (!b.bill_date) errors.push(`Bill ${b.ref_name || ""}: a date is needed`.trim());
    }
    p.opening_bills = bills.map((b) => ({ ref_name: b.ref_name.trim(), bill_date: b.bill_date, due_date: b.due_date || null, side: b.side,
                                          amount: num(b.amount, `Bill ${b.ref_name}`) }));
  }

  p.bank = k.bank && d.bank.bank_name.trim()
    ? { bank_name: d.bank.bank_name.trim(), account_no: d.bank.account_no.trim() || null, branch_code: d.bank.branch_code.trim().toUpperCase() || null,
        currency: d.bank.currency.trim().toUpperCase() || "INR" }
    : null;
  // a bank account's currency is its account's (the server keeps them together); otherwise the account's own
  if (!k.bank || !p.bank) p.currency = d.currency || null;

  if (opts.india) {
    const g = d.gstParty;
    const gstin = g.gstin.trim().toUpperCase();
    const wantsGstin = GSTIN_TYPES.includes(g.reg_type);
    if (party && g.state_code) {
      if (wantsGstin && !gstin) errors.push("GSTIN is needed for this registration type");
      if (gstin && !gstinValid(gstin)) errors.push("That GSTIN isn't valid (check the 15 characters)");
      if (gstin && gstin.slice(0, 2) !== g.state_code) errors.push("The GSTIN must start with the party's state code");
      if (g.pincode && !/^[0-9]{6}$/.test(g.pincode)) errors.push("A PIN code has 6 digits");
      if (g.eco && !gstin) errors.push("An e-commerce operator needs its GSTIN");
      p.gst_party = { reg_type: g.reg_type, gstin: wantsGstin ? gstin : null, state_code: g.state_code,
                      legal_name: g.legal_name.trim() || null, address: g.address.trim() || null, place: g.place.trim() || null,
                      pincode: g.pincode.trim() || null , ecommerce_operator: !!g.eco };
    } else {
      if (party && (gstin || wantsGstin && g.gstin)) errors.push("Choose the party's state");
      p.gst_party = null;
    }

    const gl = d.gstLedger;
    const rate = gl.rate.trim() === "" ? null : Number(gl.rate);
    if (rate != null && (Number.isNaN(rate) || rate < 0 || rate > 100)) errors.push("GST rate: a percentage");
    if (gl.hsn_sac && !/^[0-9]{4,8}$/.test(gl.hsn_sac)) errors.push("HSN / SAC: 4 to 8 digits");
    p.gst_ledger = k.pnl && (gl.hsn_sac || rate != null || gl.taxability !== "taxable" || gl.itc !== "eligible")
      ? { taxability: gl.taxability, hsn_sac: gl.hsn_sac || null, rate, itc: gl.itc } : null;

    const t = d.gstTax;
    if (k.duties && t.on && !t.registration_id) errors.push("Choose the GST registration this tax ledger is for");
    p.gst_tax = k.duties && t.on && t.registration_id ? { registration_id: t.registration_id, head: t.head, direction: t.direction, rcm: t.rcm } : null;

    const tp = d.tdsParty;
    const pan = tp.pan.trim().toUpperCase();
    if (pan && !panValid(pan)) errors.push("That PAN isn't valid (5 letters, 4 digits, a letter)");
    const nr = tp.residence === "non_resident";
    const treaty = tp.treaty_rate.trim() === "" ? null : Number(tp.treaty_rate);
    if (treaty != null && (Number.isNaN(treaty) || treaty < 0 || treaty > 100)) errors.push("Treaty rate: a percentage");
    const anyTds = pan || nr || tp.default_provision || tp.senior_citizen || tp.transporter_declaration;
    p.tds_party = party && anyTds ? {
      pan: pan || null, residence: tp.residence, default_provision: tp.default_provision || null, senior_citizen: tp.senior_citizen,
      transporter_declaration: tp.transporter_declaration,
      country: nr ? tp.country.trim().toUpperCase() || null : null, trc_valid_to: nr ? tp.trc_valid_to || null : null,
      form_10f: nr ? tp.form_10f : false, treaty_rate: nr ? treaty : null, foreign_tax_id: nr ? tp.foreign_tax_id.trim() || null : null,
    } : null;
    p.tds_ledger = k.pnl && d.tdsLedger.provision_code ? { provision_code: d.tdsLedger.provision_code } : null;
  }
  return { payload: p, errors: [...new Set(errors)] };
}

// the opening bills' total against the opening balance (signed, Dr +)
export function billsDifference(d: Draft): number {
  const sgn = (side: Side, v: string) => { const n = parseAmount(v); return n == null || Number.isNaN(n) ? 0 : side === "Cr" ? -n : n; };
  const opening = sgn(d.opening.side, d.opening.amount);
  const bills = d.bills.reduce((t, b) => t + sgn(b.side, b.amount), 0);
  return Math.round((opening - bills) * 100) / 100;
}
